import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '../components/common/Seo';
import BlogCard from '../components/common/BlogCard';
import ReadingProgress from '../components/common/ReadingProgress';
import ShareRow from '../components/common/ShareRow';
import NotFound from './NotFound';
import { fetchBlogById, fetchRelatedBlogs } from '../api/blogs';
import { blogPath, categoryFromSlug, extractIdFromSlug, toSlug } from '../utils/slug';
import { imageFallback } from '../utils/env';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  formatDate,
  isoDate,
  metaDescription,
  modifiedAt,
  publishedAt,
  readingMinutes,
} from '../utils/post';
import { articleSchema, breadcrumbSchema } from '../utils/jsonLd';
import { SITE_URL } from '../constants/site';

function ArticleSkeleton() {
  return (
    <div className="container page article" aria-busy="true" aria-label="Loading article">
      <span className="skeleton skeleton-line skeleton-line-sm" />
      <span className="skeleton skeleton-line skeleton-line-xl" />
      <span className="skeleton skeleton-line skeleton-line-lg" />
      <div className="skeleton skeleton-hero" />
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} className="skeleton skeleton-line" />
      ))}
    </div>
  );
}

function BlogDetailPage() {
  const { categorySlug, slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const id = extractIdFromSlug(slug);

    setLoading(true);
    setNotFound(false);
    setBlog(null);
    setRelated([]);

    // The category is passed so fetchBlogById can fall back to the composite
    // /api/blogs/{category}/{id} path if the flat one is not implemented.
    fetchBlogById(id, categoryFromSlug(categorySlug))
      .then(async (data) => {
        if (cancelled) return;
        if (!data) {
          setNotFound(true);
          return;
        }
        setBlog(data);
        const rel = await fetchRelatedBlogs(data, 3);
        if (!cancelled) setRelated(rel);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, slug]);

  /*
   * Sanitise on READ as well as on write.
   *
   * The editor sanitises before saving, but that only covers content this app
   * wrote. Anything already in the store — posted straight to the API, migrated
   * in, or saved before the editor did this — reaches dangerouslySetInnerHTML
   * untouched, and an <img src=x onerror=…> in a stored body executes on the
   * public article page. Body images are unaffected: <img src/alt/width/height>
   * is on the allowlist, so they render exactly where the author placed them.
   *
   * Above the early returns, because hooks cannot run conditionally.
   */
  const safeContent = useMemo(() => sanitizeHtml(blog?.content || ''), [blog]);

  if (loading) return <ArticleSkeleton />;
  if (notFound || !blog) return <NotFound />;

  // Always the slug this app would generate, so a crawl of an older or
  // differently-slugged URL consolidates onto one canonical page.
  const canonicalPath = blogPath(blog);
  const published = publishedAt(blog);
  const modified = modifiedAt(blog);
  const minutes = readingMinutes(blog);
  const categoryHref = `/category/${toSlug(blog.category)}`;

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Articles', path: '/blogs' },
    { name: blog.category, path: categoryHref },
    { name: blog.title, path: canonicalPath },
  ];

  return (
    <>
      <ReadingProgress />

      <Seo
        title={blog.title}
        description={metaDescription(blog)}
        path={canonicalPath}
        image={blog.image}
        type="article"
        publishedTime={isoDate(published)}
        modifiedTime={isoDate(modified)}
        author={blog.author}
        section={blog.category}
        tags={blog.tags}
        jsonLd={[articleSchema(blog), breadcrumbSchema(crumbs)]}
      />

      <div className="container page">
        <article className="article">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/blogs">Articles</Link>
              </li>
              <li>
                <Link to={categoryHref}>{blog.category}</Link>
              </li>
            </ol>
          </nav>

          <header className="article-head">
            <Link className="chip chip-link" to={categoryHref}>
              {blog.category}
            </Link>

            <h1 className="article-title">{blog.title}</h1>

            {blog.excerpt && <p className="article-standfirst">{blog.excerpt}</p>}

            <div className="article-meta">
              <span className="article-author">{blog.author}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={isoDate(published)}>{formatDate(published)}</time>
              <span aria-hidden="true">·</span>
              <span>{minutes} min read</span>
              {modified && modified !== published && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="article-updated">
                    Updated <time dateTime={isoDate(modified)}>{formatDate(modified)}</time>
                  </span>
                </>
              )}
            </div>
          </header>

          {blog.image && (
            <img
              className="article-hero"
              src={blog.image}
              alt=""
              width="1200"
              height="675"
              fetchpriority="high"
              decoding="async"
              onError={imageFallback('/default-blog.svg')}
            />
          )}

          {/* Allowlist-sanitised immediately above — see safeContent. */}
          <div
            className="article-body"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />

          {blog.tags?.length > 0 && (
            <div className="article-tags" aria-label="Tags">
              {blog.tags.map((tag) => (
                <span key={tag} className="chip chip-sm">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {blog.urlLink && (
            <p className="article-source">
              Source:{' '}
              <a href={blog.urlLink} target="_blank" rel="noopener noreferrer nofollow">
                {blog.urlLink}
              </a>
            </p>
          )}

          <ShareRow url={`${SITE_URL}${canonicalPath}`} title={blog.title} />

          <div className="article-footer-nav">
            <Link className="btn btn-ghost" to={categoryHref}>
              <span aria-hidden="true">←</span> More in {blog.category}
            </Link>
          </div>
        </article>

        {related.length > 0 && (
          <section className="related" aria-labelledby="related-heading">
            <h2 className="section-title" id="related-heading">
              Related articles
            </h2>
            <div className="blog-grid blog-grid-related">
              {related.map((b) => (
                <BlogCard key={b.blogId} blog={b} variant="compact" headingLevel="h3" />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default BlogDetailPage;
